import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Select from '../../components/ui/Select';
import { getErrorMessage } from '../../lib/apiClient';
import { getContents, fileKeys } from '../files/filesApi';
import { createFileRequest } from './fileRequestApi';

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 604800 },
  { label: '30 days', value: 2592000 },
  { label: '90 days', value: 7776000 },
  { label: 'No expiry', value: 0 },
];

export default function CreateFileRequestModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [folderId, setFolderId] = useState('');
  const [expiresInSeconds, setExpiresInSeconds] = useState(2592000);
  const [password, setPassword] = useState('');
  const [maxFiles, setMaxFiles] = useState('');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState('');
  const [allowedTypes, setAllowedTypes] = useState('');

  // Top-level folders as collect targets (defensive about the response shape).
  const { data: contents } = useQuery({
    queryKey: fileKeys.contents(null, { forRequest: true }),
    queryFn: () => getContents(null),
    enabled: open,
  });
  const folders = contents?.folders ?? contents?.data?.folders ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      createFileRequest({
        title,
        instructions: instructions || undefined,
        recipientEmail: recipientEmail.trim() || undefined,
        folderId: folderId || undefined,
        expiresInSeconds: expiresInSeconds || undefined,
        password: password || undefined,
        maxFiles: maxFiles ? Number(maxFiles) : undefined,
        maxFileSizeBytes: maxFileSizeMb ? Math.round(Number(maxFileSizeMb) * 1024 * 1024) : undefined,
        allowedTypes: allowedTypes.trim()
          ? allowedTypes.split(',').map((t) => t.trim().replace(/^\./, '').toLowerCase()).filter(Boolean)
          : undefined,
      }),
    onSuccess: (data) => {
      toast.success('File request created');
      onCreated?.({ ...data, recipientEmail: recipientEmail.trim() || null });
      reset();
      onClose?.();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not create the request')),
  });

  const reset = () => {
    setTitle('');
    setInstructions('');
    setRecipientEmail('');
    setFolderId('');
    setPassword('');
    setMaxFiles('');
    setMaxFileSizeMb('');
    setAllowedTypes('');
    setExpiresInSeconds(2592000);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New file request"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={mutation.isPending}
            disabled={!title.trim()}
            onClick={() => mutation.mutate()}
          >
            Create request
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Please upload your documents"
          autoFocus
        />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Instructions (optional)</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={2}
            className="block w-full resize-y rounded-lg border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-600 focus:outline-none"
            placeholder="What should people upload?"
          />
        </div>
        <Input
          label="Request Files From (Recipient Email)"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="uploader@example.com"
          hint="They'll receive the upload link by email — no account needed"
        />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Collect into</label>
          <Select
            value={folderId}
            onChange={(v) => setFolderId(v)}
            options={[
              { value: '', label: 'Workspace root' },
              ...folders.map((f) => ({ value: f.id, label: f.name })),
            ]}
            className="w-full"
            ariaLabel="Collect into"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Expires</label>
            <Select
              value={String(expiresInSeconds)}
              onChange={(v) => setExpiresInSeconds(Number(v))}
              options={EXPIRY_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
              className="w-full"
              ariaLabel="Expires"
            />
          </div>
          <Input
            label="Max files"
            type="number"
            min={1}
            value={maxFiles}
            onChange={(e) => setMaxFiles(e.target.value)}
            placeholder="Unlimited"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Max size / file (MB)"
            type="number"
            min={1}
            value={maxFileSizeMb}
            onChange={(e) => setMaxFileSizeMb(e.target.value)}
            placeholder="No limit"
          />
          <Input
            label="Password (optional)"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Input
          label="Allowed types (optional)"
          value={allowedTypes}
          onChange={(e) => setAllowedTypes(e.target.value)}
          placeholder="pdf, png, docx"
          hint="Comma-separated extensions; leave blank to allow any"
        />
      </div>
    </Modal>
  );
}
